// Need to use the React-specific entry point to import createApi
/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateUserRequest,
  CreateUserResponse,
  PaymentMethodsResponse,
  PaymentMethodsResponseTransformed,
  UserMe,
  ValidateEmailRequest,
  ValidateEmailResponse,
} from '@shared/types';
import { transformResponse } from '@utilities/api';
import fetchBaseQuery from '@utilities/fetchBaseQuery';
import { commonApi } from './common';

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: 'userApi',
  tagTypes: ['User', 'Membership', 'Wash', 'Invoice'],
  baseQuery: fetchBaseQuery,
  endpoints: (builder) => ({
    /**
     *
     * GET QUERIES
     */

    /**
     * Get a user
     */
    getUser: builder.query({
      query: (id) => `users/${id}`,
      transformResponse,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    /**
     * Get me
     */
    getMe: builder.query<UserMe, null>({
      query: () => `user/me`,
      transformResponse,
      providesTags: (result) => [{ type: 'User', id: result?.uid }],
    }),

    /**
     * Get Washoverview
     */
    getWashOverview: builder.query({
      query: () => `user/me/wash-overview?page=1&limit=5`,
      transformResponse: (response) => response.wash_overview,
    }),

    /**
     * Get Washoverview with limit
     */
    getWashOverviewLimit: builder.query({
      query: ({ page = 1, limit = 5 }) =>
        `user/me/wash-overview?page=${page}&limit=${limit}`,
      transformResponse: (response) => ({
        washOverviewList: response?.wash_overview || null,
        pagination: response?.pagination || null,
      }),
    }),

    /**
     * Get Wash Voucher
     */
    getWashVouchers: builder.query({
      query: () => `user/me/campaigns/sw`,
      transformResponse: (response) => response?.response || response,
    }),

    /**
     * Get invoices with limit
     */
    getInvoicesLimit: builder.query({
      query: (page = 1) => `/user/me/invoices?page=${page}&limit=10`,
      transformResponse: (response) => ({
        invoices: response?.invoices || null,
        pagination: response?.pagination || null,
      }),
      providesTags: (result) => [
        { type: 'Invoice', id: result?.invoices?.invoice },
      ],
    }),

    /**
     * Get invoices with limit
     */
    getPlanCost: builder.query({
      query: (uid) => `/user/me/memberships/${uid}/plancost`,
      transformResponse: (response) => ({
        planCost: response?.response || null,
      }),
    }),

    /**
     * Check if user can update license plate
     */
    getLPCheck: builder.query({
      query: (uid) => `/membership/${uid}/update-licenseplate`,
      transformResponse,
      providesTags: (result) => [{ type: 'Membership', id: result?.uid }],
    }),

    /**
     * Check if user can update location
     */
    getLocCheck: builder.query({
      query: (uid) => `/membership/${uid}/update-location`,
      transformResponse: (response) => ({
        isChangeable: response?.response?.changeAble || null,
        changeableDate: response?.response?.changeable_date || null,
      }),
      providesTags: (result) => [{ type: 'Membership', id: result?.uid }],
    }),

    /**
     * Cancel downgrade
     */
    getCancelDownGrade: builder.query({
      query: (uid) => `/membership/${uid}/cancel-downgrade`,
      transformResponse: (response) => response,
    }),

    /**
     * Update user password
     */
    cancelDownGrade: builder.mutation({
      query: (uid) => ({
        url: `/membership/${uid}/cancel-downgrade`,
        method: 'GET',
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Get payment session
     */
    getPaymentSession: builder.query({
      query: (walletid) => ({
        url: `/sessions/${walletid}`,
        method: 'GET',
      }),
      transformResponse,
    }),

    /**
     * Get payment session dunning
     */
    getPaymentSessionDunning: builder.query({
      query: ({ walletid, type }) => ({
        url: `/sessions/${walletid}/${type}`,
        method: 'GET',
      }),
      transformResponse,
    }),

    /**
     * Get user credits
     */
    getUserCredit: builder.query({
      query: () => 'user/me/credits',
      providesTags: (_result) => [{ type: 'User', id: _result?.uid }],
      transformResponse,
    }),

    /**
     *
     * PUT QUERIES
     */

    /**
     * Update a user
     */
    updateUser: builder.mutation({
      query: (body) => {
        if (!body.keepPayload) {
          if (body.phone) {
            body.phone_prefix = body.phone.substring(1, 3);
            body.phone = body.phone.slice(3).trim();
          }
        }
        return {
          url: `user/me`,
          method: 'PUT',
          body,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate the Messages tag from the membershipsApi
          dispatch(commonApi.util.invalidateTags(['UserSubscriptions']));
        } catch (error) {
          console.error('Error invalidating tags:', error);
        }
      },
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Update user password
     */
    updatePassword: builder.mutation({
      query: (body) => ({
        url: `user/me/password`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Update user password
     */
    updatePinCode: builder.mutation({
      query: (body) => ({
        url: `user/me/pin`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Update user subscriptions
     */
    updateSubscriptions: builder.mutation({
      query: (body) => {
        const subscriptions = {
          marketing_20211201: body.consent,
        };
        return {
          url: `user/me/subscriptions`,
          method: 'PUT',
          body: subscriptions,
        };
      },
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Add memberships to user
     */
    addMemberships: builder.mutation({
      query: (body) => ({
        url: 'membership/add-user',
        method: 'PUT',
        body: { memberships: body },
      }),
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     *
     * POST QUERIES
     */

    /**
     * Create user
     */

    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: (body) => ({
        url: `user`,
        method: 'POST',
        body: {
          ...body,
          // We use checkout as default for signup
          identityProvider:
            body.identityProvider !== undefined
              ? body.identityProvider
              : 'checkout',
          ...(body.phone && {
            phone_prefix: body.phone.substring(1, 3),
            phone: body.phone.slice(3).trim(),
          }),
        },
      }),
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Validate if e-mail exists
     */

    validateEmailIsUnique: builder.mutation<
      ValidateEmailResponse,
      ValidateEmailRequest
    >({
      query: (email) => ({
        url: `user/validate-email`,
        method: 'POST',
        body: {
          email,
        },
      }),
      transformResponse,
    }),

    /**
     * Validate phone number
     */

    validatePhoneNumber: builder.mutation({
      query: (phone) => ({
        url: `user/validate-phone`,
        method: 'POST',
        body: {
          phone_prefix: phone.substring(1, 3),
          phone: phone.slice(3).trim(),
        },
      }),
      transformResponse,
    }),

    /**
     * Delete user
     */

    deleteProfile: builder.mutation({
      query: () => ({
        url: `user/me`,
        method: 'DELETE',
      }),
      transformResponse,
    }),

    /**
     * Get payment methods
     */
    getPaymentMethods: builder.query<PaymentMethodsResponseTransformed, void>({
      query: () => '/adyen/payment_methods',
      transformResponse: (response: PaymentMethodsResponse, meta) => ({
        data: response?.response || response,
        meta,
      }),
    }),

    /**
     * Post payment
     */
    postPayment: builder.mutation({
      query: (body) => ({
        url: '/adyen/payment',
        method: 'POST',
        body,
      }),
      transformResponse,
      invalidatesTags: ['User'],
    }),

    /**
     * Check payment
     */
    checkPayment: builder.mutation({
      query: (body) => ({
        url: '/adyen/check-payment',
        method: 'POST',
        body,
      }),
      transformResponse,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useGetMeQuery,

  useGetWashOverviewQuery,
  useGetWashOverviewLimitQuery,
  useGetWashVouchersQuery,
  useGetInvoicesLimitQuery,

  useGetPaymentSessionQuery,
  useGetPaymentSessionDunningQuery,

  useGetPlanCostQuery,
  useGetUserCreditQuery,

  useUpdateUserMutation,
  useUpdatePasswordMutation,
  useUpdatePinCodeMutation,
  useUpdateSubscriptionsMutation,
  useAddMembershipsMutation,

  useCreateUserMutation,

  useValidateEmailIsUniqueMutation,
  useValidatePhoneNumberMutation,

  useDeleteProfileMutation,

  useGetPaymentMethodsQuery,
  usePostPaymentMutation,
  useCheckPaymentMutation,
} = userApi;
